import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { CalendarEventDto } from "components/visits/types";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { agendaSlice } from "store/slices/agenda";
import { AgendaDueRow, computeNewlyCompleted } from "./marketing-plan-agenda";

interface AgendaSnoozeRow {
    contactId: string;
    touchpointId: string | null;
}

type AgendaTrpcClient = ReturnType<typeof trpc.useTRPC>;

const agendaQueryKeys = (trpcClient: AgendaTrpcClient) => ({
    dueKey: trpcClient.marketingPlaybook.dueTouchpoints.queryKey(),
    snoozeKey: trpcClient.marketingPlaybook.activeSnoozes.queryKey(),
    calendarKey: trpcClient.calendarEvents.listForCalendar.queryKey(),
    contactPlanKey: trpcClient.marketingPlaybook.contactPlan.queryKey(),
});

const invalidateAgendaQueries = async (
    trpcClient: AgendaTrpcClient,
    queryClient: QueryClient,
): Promise<void> => {
    const { dueKey, snoozeKey, calendarKey, contactPlanKey } =
        agendaQueryKeys(trpcClient);
    await Promise.all(
        [dueKey, snoozeKey, calendarKey, contactPlanKey].map(queryKey =>
            queryClient.invalidateQueries({ queryKey }),
        ),
    );
};

export const useAgendaRefresh = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    return useCallback(
        () => invalidateAgendaQueries(trpcClient, queryClient),
        [trpcClient, queryClient],
    );
};

export const useAgendaRefreshWithCompletionDetection = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    return useCallback(async () => {
        const { dueKey, snoozeKey, calendarKey } = agendaQueryKeys(trpcClient);
        const readDue = (): AgendaDueRow[] => {
            const query = queryClient
                .getQueryCache()
                .findAll({ queryKey: dueKey, type: "active" })[0];
            return (query?.state.data as AgendaDueRow[] | undefined) ?? [];
        };
        const readUpcomingVisitContactIds = (): Set<string> => {
            const query = queryClient
                .getQueryCache()
                .findAll({ queryKey: calendarKey, type: "active" })[0];
            const events = (query?.state.data as CalendarEventDto[] | undefined) ?? [];
            return new Set(events.map(event => event.parentId));
        };
        const before = readDue();
        await invalidateAgendaQueries(trpcClient, queryClient);
        const after = readDue();
        const snoozeQuery = queryClient
            .getQueryCache()
            .findAll({ queryKey: snoozeKey, type: "active" })[0];
        const snoozes = (snoozeQuery?.state.data as AgendaSnoozeRow[] | undefined) ?? [];
        const completed = computeNewlyCompleted(
            before,
            after,
            snoozes.map(snooze => ({
                contactId: snooze.contactId,
                touchpointId: snooze.touchpointId,
            })),
            readUpcomingVisitContactIds(),
        );
        if (completed.length > 0) {
            dispatch(agendaSlice.actions.reportRecentlyCompleted(completed));
        }
    }, [trpcClient, queryClient, dispatch]);
};
